import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../api/httpClient';
import { getApiBaseUrl } from '../../data/authStorage';
import useCurrentEmployee from '../../hooks/useCurrentEmployee';
import FetchLoadingState from '../../components/feedback/FetchLoadingState';
import { readSessionCache, writeSessionCache } from '../../utils/sessionCache';
import { FiArrowLeft } from 'react-icons/fi';

const api = `${getApiBaseUrl()}/organizations/contract-staff`;
const CACHE_KEY = 'cehr_contract_staff';
const messageOf = (error) => {
  const message = error?.response?.data?.message || error?.message || 'Request failed';
  return Array.isArray(message) ? message.join(', ') : message;
};
const canManage = (account, employee) => account || employee?.isHeadOfDepartment ||
  employee?.modulePermissions?.includes('contract-staff') ||
  /\b(hod|head of department)\b/i.test([employee?.positionName, employee?.position?.name, employee?.roleName].filter(Boolean).join(' '));

export default function ContractStaffPage() {
  const navigate = useNavigate();
  const { companyAccount, currentEmployee, loading: identityLoading } = useCurrentEmployee();
  const permitted = canManage(companyAccount, currentEmployee);
  const cachedRecords = useMemo(() => readSessionCache(CACHE_KEY, null), []);
  const [records, setRecords] = useState(cachedRecords?.records || []);
  const [loading, setLoading] = useState(!cachedRecords);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [menuId, setMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  useEffect(() => {
    const closeMenu = (event) => {
      if (!event.target.closest('[data-contract-menu]') && !event.target.closest('[data-contract-menu-trigger]')) {
        setMenuId(null);
      }
    };
    window.addEventListener('click', closeMenu);
    return () => window.removeEventListener('click', closeMenu);
  }, []);
  const load = useCallback(async ({ force = false } = {}) => {
    if (!permitted) { setLoading(false); return; }
    const cached = readSessionCache(CACHE_KEY, null);
    if (cached && !force) {
      setRecords(cached.records || []);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(api);
      const nextRecords = data?.contractors || [];
      setRecords(nextRecords);
      writeSessionCache(CACHE_KEY, { records: nextRecords });
      setError('');
    } catch (requestError) { setError(messageOf(requestError)); }
    finally { setLoading(false); }
  }, [permitted]);
  useEffect(() => { load(); }, [load]);
  const shown = useMemo(() => records.filter((record) => {
    const text = [record.firstName, record.lastName, record.officialEmail, record.employeeNumber, record.department?.name].join(' ').toLowerCase();
    return (filter === 'All' || record.accessStatus === filter) && text.includes(search.toLowerCase());
  }), [records, filter, search]);
  const changeStatus = async () => {
    if (!confirmAction || actionBusy) return;
    const { record, status } = confirmAction;
    setActionBusy(true);
    try { await axios.patch(`${api}/${record.id}`, { status }); await load({ force: true }); setConfirmAction(null); }
    catch (requestError) { setError(messageOf(requestError)); }
    finally { setActionBusy(false); }
  };
  const handleNavigate = (path) => {
    setMenuId(null);
    navigate(path);
  };
  const openMenu = (event, recordId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuId((current) => current === recordId ? null : recordId);
    setMenuPosition({
      top: rect.bottom + 8 + window.scrollY,
      left: rect.right - 208,
    });
  };
  if (identityLoading || loading) return <FetchLoadingState message="Loading contract staff..." fullScreen />;
  if (!permitted) return <div className="p-6 text-red-700">Contract staff management access is required.</div>;
  return <div className="min-w-0 space-y-5 p-3 font-Nunito sm:p-6">
    <Link to="/employee" className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-[#1234CD] hover:text-[#1234CD]">
      <FiArrowLeft aria-hidden="true" /> Back to employees
    </Link>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div><h1 className="text-2xl font-bold text-slate-900">Contract staff</h1><p className="text-sm text-slate-500">Contracts, assignments, work reviews and tool custody.</p></div>
      <button type="button" onClick={() => handleNavigate('/contract-staff/new')} className="w-full rounded-xl bg-[#1234CD] px-5 py-2.5 font-semibold text-white sm:w-auto">Add contract staff</button>
    </div>
    {error && <div role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</div>}
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center">
      {['All', 'Active', 'Scheduled', 'Inactive'].map((status) => <button type="button" key={status} onClick={() => setFilter(status)} className={`rounded-xl border px-4 py-2 text-sm ${filter === status ? 'border-[#1234CD] bg-[#1234CD] text-white' : 'border-slate-300 bg-white'}`}>{status}</button>)}
      <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search contract staff" aria-label="Search contract staff" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm md:ml-auto md:max-w-xs" />
    </div>
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full min-w-[760px] text-left text-sm"><thead className="bg-slate-50 text-slate-600"><tr>{['Name', 'Department', 'Contract', 'Projects', 'Access', 'Ends', 'Actions'].map((label) => <th key={label} className="px-4 py-3 font-semibold">{label}</th>)}</tr></thead>
        <tbody>{shown.map((record) => { const agreement = record.contractAgreements?.[0]; return <tr key={record.id} className="border-t border-slate-100 align-top">
          <td className="px-4 py-4"><Link to={`/contract-staff/${record.id}`} className="font-semibold text-[#1234CD] hover:underline">{record.firstName} {record.lastName}</Link><div className="text-xs text-slate-400">{record.employeeNumber}</div></td>
          <td className="px-4 py-4">{record.department?.name || '—'}</td>
          <td className="px-4 py-4">{agreement?.title || '—'}<div className="text-slate-500">{agreement?.contractNumber}</div></td>
          <td className="max-w-[220px] px-4 py-4">{record.projects?.map((project) => project.title).join(', ') || 'None'}</td>
          <td className="px-4 py-4">{record.accessStatus}</td><td className="px-4 py-4">{agreement?.endsAt ? new Date(agreement.endsAt).toLocaleDateString() : '—'}</td>
          <td className="px-4 py-4">
            <div className="relative inline-block text-left">
              <button
                type="button"
                data-contract-menu-trigger
                onClick={(event) => {
                  event.stopPropagation();
                  openMenu(event, record.id);
                }}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-lg leading-none text-slate-600 shadow-sm transition hover:border-[#1234CD] hover:text-[#1234CD]"
                aria-label={`Open actions for ${record.firstName} ${record.lastName}`}
                title="Actions"
              >
                <span className="flex flex-col items-center justify-center gap-[2px]">
                  <span className="h-1 w-1 rounded-full bg-current" />
                  <span className="h-1 w-1 rounded-full bg-current" />
                  <span className="h-1 w-1 rounded-full bg-current" />
                </span>
              </button>
              {menuId === record.id && <div data-contract-menu style={{ position: 'fixed', top: menuPosition.top, left: menuPosition.left, zIndex: 1000 }} className="w-52 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                <button type="button" onClick={() => handleNavigate(`/contract-staff/${record.id}`)} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">Work & payments</button>
                <button type="button" onClick={() => handleNavigate(`/contract-staff/${record.id}/edit`)} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">Edit</button>
                <button type="button" onClick={() => handleNavigate(`/contract-staff/${record.id}/renew`)} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">New contract</button>
                {agreement?.status === 'Active' && <button type="button" onClick={() => setConfirmAction({ record, status: 'Suspended' })} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">Suspend</button>}
                {agreement?.status === 'Suspended' && <button type="button" onClick={() => setConfirmAction({ record, status: 'Active' })} className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-slate-50">Resume</button>}
                {agreement?.status !== 'Terminated' && <button type="button" onClick={() => setConfirmAction({ record, status: 'Terminated' })} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50">Terminate</button>}
              </div>}
            </div>
          </td>
        </tr>; })}</tbody></table>
      {!shown.length && <p className="p-8 text-center text-slate-500">No contract staff found.</p>}
    </div>
    {confirmAction && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"><div role="alertdialog" aria-modal="true" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"><h2 className="text-lg font-bold">Confirm access change</h2><p className="mt-2 text-sm">Set {confirmAction.record.firstName}'s contract to {confirmAction.status}?</p><div className="mt-6 flex justify-end gap-3"><button disabled={actionBusy} onClick={() => setConfirmAction(null)} className="rounded-xl border px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50">Cancel</button><button disabled={actionBusy} onClick={changeStatus} className="rounded-xl bg-[#1234CD] px-4 py-2 text-white disabled:cursor-not-allowed disabled:opacity-60">{actionBusy ? `${confirmAction.status === 'Terminated' ? 'Terminating' : confirmAction.status === 'Suspended' ? 'Suspending' : 'Updating'}...` : 'Confirm'}</button></div></div></div>}
  </div>;
}
